"use client";

import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Textarea } from "@/app/components/ui/textarea";
import { Input } from "@/app/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/components/ui/select";
import { Badge } from "@/app/components/ui/badge";
import { Progress } from "@/app/components/ui/progress";
import { Alert, AlertDescription } from "@/app/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { Bug, Shield, AlertTriangle, CheckCircle, XCircle, Download, RotateCcw, Zap, Upload, File } from "lucide-react";
import FileUpload from "@/app/components/FileUpload";
import { trpc } from "@/lib/trpc";

interface Vulnerability {
  type: 'xss' | 'sqli' | 'malware' | 'info';
  severity: 'high' | 'medium' | 'low';
  description: string;
  payload?: string;
  recommendation: string;
  line?: number;
  file?: string;
}

interface ScanResult {
  vulnerabilities: Vulnerability[];
  totalIssues: number;
  highIssues: number;
  mediumIssues: number;
  lowIssues: number;
  scanTime: number;
  fileInfo?: {
    name: string;
    size: number;
    type: string;
    hash: string;
  };
}

export default function VulnerabilityScannerPage() {
  const [code, setCode] = useState("");
  const [scanType, setScanType] = useState<"xss" | "sqli" | "both">("both");
  const [fileScanType, setFileScanType] = useState<"malware" | "code" | "both">("both");
  const [result, setResult] = useState<ScanResult | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"code" | "file">("code");

  // TRPC mutations
  const scanCodeMutation = trpc.vulnerabilityScanner.scanCode.useMutation();
  const scanFileMutation = trpc.vulnerabilityScanner.scanFile.useMutation();
  const statsQuery = trpc.vulnerabilityScanner.getStats.useQuery();

  const sampleCode = `<?php
// Sample vulnerable code for testing
$user_input = $_GET['search'];
$query = "SELECT * FROM users WHERE name = '$user_input'";
$result = mysql_query($query);

echo "<div>Welcome " . $_POST['username'] . "</div>";
echo "<script>var user = '" . $_GET['user'] . "';</script>";
?>`;


  const scanCode = async () => {
    if (!code.trim()) {
      setError("Please enter some code to scan");
      return;
    }

    setIsScanning(true);
    setError("");
    setResult(null);

    try {
      const result = await scanCodeMutation.mutateAsync({
        code,
        scanType
      });
      setResult(result);
    } catch (error: any) {
      setError(error.message || "Failed to scan code. Please try again.");
    } finally {
      setIsScanning(false);
    }
  };

  const scanFile = async () => {
    if (!uploadedFile && !uploadedFileUrl) {
      setError("Please select a file to scan");
      return;
    }

    setIsScanning(true);
    setError("");
    setResult(null);

    try {
      let result: ScanResult;

      if (uploadedFileUrl) {
        // Use uploaded file
        const response = await fetch(uploadedFileUrl);
        const fileBuffer = await response.arrayBuffer();
        const base64 = btoa(String.fromCharCode(...new Uint8Array(fileBuffer)));
        
        // Extract filename from URL or use a default
        const filename = uploadedFileUrl.split('/').pop() || 'uploaded-file';
        
        result = await scanFileMutation.mutateAsync({
          fileBase64: base64,
          filename: filename,
          contentType: 'application/octet-stream',
          scanType: fileScanType
        });
      } else if (uploadedFile) {
        // Use local file
        const fileBuffer = await uploadedFile.arrayBuffer();
        const base64 = btoa(String.fromCharCode(...new Uint8Array(fileBuffer)));

        result = await scanFileMutation.mutateAsync({
          fileBase64: base64,
          filename: uploadedFile.name,
          contentType: uploadedFile.type,
          scanType: fileScanType
        });
      } else {
        throw new Error("No file available for scanning");
      }

      setResult(result);
    } catch (error: any) {
      setError(error.message || "Failed to scan file. Please try again.");
    } finally {
      setIsScanning(false);
    }
  };

  const handleUploadComplete = (fileUrl: string, fileName: string, fileSize: number, fileType: string) => {
    setUploadedFileUrl(fileUrl);
    setError("");
    setResult(null);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'low': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const getVulnerabilityIcon = (type: string) => {
    switch (type) {
      case 'xss': return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      case 'sqli': return <Bug className="h-4 w-4 text-red-500" />;
      case 'malware': return <XCircle className="h-4 w-4 text-red-600" />;
      case 'info': return <Shield className="h-4 w-4 text-blue-500" />;
      default: return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const downloadReport = () => {
    if (!result) return;

    const report = {
      scanType,
      timestamp: new Date().toISOString(),
      results: result,
      code: code
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vulnerability-scan-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const clearAll = () => {
    setCode("");
    setUploadedFile(null);
    setUploadedFileUrl(null);
    setResult(null);
    setError("");
  };

  const loadSampleCode = () => {
    setCode(sampleCode);
    setError("");
    setResult(null);
  };

  return (
    <div className="container mx-auto px-4 py-8 animate-fade-in">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4 animate-slide-in-left">XSS/SQLi Scanner</h1>
          <p className="text-muted-foreground text-lg animate-slide-in-right">
            Quick check for common injection vectors in web applications
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input */}
          <Card className="card-enhanced">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bug className="h-5 w-5" />
                <span>Scanner Input</span>
              </CardTitle>
              <CardDescription>
                Scan code or files for vulnerabilities and malware
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "code" | "file")}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="code">Code Scan</TabsTrigger>
                  <TabsTrigger value="file">File Scan</TabsTrigger>
                </TabsList>
                
                <TabsContent value="code" className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Scan Type</label>
                    <Select value={scanType} onValueChange={(value: "xss" | "sqli" | "both") => setScanType(value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="both">XSS & SQL Injection</SelectItem>
                        <SelectItem value="xss">XSS Only</SelectItem>
                        <SelectItem value="sqli">SQL Injection Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Textarea
                    placeholder="Paste your code here to scan for vulnerabilities..."
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className="min-h-[300px] font-mono text-sm input-enhanced"
                  />
                  
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      onClick={scanCode} 
                      className="btn-enhanced hover-lift"
                      disabled={!code.trim() || isScanning}
                    >
                      {isScanning ? (
                        <>
                          <Zap className="h-4 w-4 mr-2 animate-spin" />
                          Scanning...
                        </>
                      ) : (
                        <>
                          <Bug className="h-4 w-4 mr-2" />
                          Scan Code
                        </>
                      )}
                    </Button>
                    <Button 
                      onClick={loadSampleCode} 
                      variant="outline" 
                      className="hover-lift"
                    >
                      Load Sample
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="file" className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Scan Type</label>
                    <Select value={fileScanType} onValueChange={(value: "malware" | "code" | "both") => setFileScanType(value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="both">Malware & Code Analysis</SelectItem>
                        <SelectItem value="malware">Malware Only</SelectItem>
                        <SelectItem value="code">Code Analysis Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <FileUpload
                    onFileChangeAction={setUploadedFile}
                    onUploadComplete={handleUploadComplete}
                    accept="*/*"
                    maxSize={100}
                    placeholder="Upload a file to scan for malware and vulnerabilities"
                    file={uploadedFile}
                  />

                  {statsQuery.data && (
                    <div className="text-xs text-muted-foreground space-y-1">
                      <p>Max file size: {Math.round(statsQuery.data.maxFileSize / 1024 / 1024)}MB</p>
                      <p>Supported types: {statsQuery.data.supportedFileTypes.slice(0, 3).join(", ")}...</p>
                      <p>Antivirus engines: {statsQuery.data.antivirusEngines.join(", ")}</p>
                    </div>
                  )}
                  
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      onClick={scanFile} 
                      className="btn-enhanced hover-lift"
                      disabled={(!uploadedFile && !uploadedFileUrl) || isScanning}
                    >
                      {isScanning ? (
                        <>
                          <Zap className="h-4 w-4 mr-2 animate-spin" />
                          Scanning...
                        </>
                      ) : (
                        <>
                          <Shield className="h-4 w-4 mr-2" />
                          Scan File
                        </>
                      )}
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>

              <div className="flex flex-wrap gap-2">
                <Button 
                  onClick={clearAll} 
                  variant="outline" 
                  className="hover-lift"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Clear All
                </Button>
              </div>

              {error && (
                <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800 dark:text-red-200">
                    {error}
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Results */}
          <Card className="card-enhanced">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                <span>Scan Results</span>
                {result && (
                  <Badge variant="outline" className={
                    result.highIssues > 0 ? "text-red-600" : 
                    result.mediumIssues > 0 ? "text-yellow-600" : "text-green-600"
                  }>
                    {result.totalIssues} issues found
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                Vulnerability analysis results
              </CardDescription>
            </CardHeader>
            <CardContent>
              {result ? (
                <div className="space-y-6">
                  {/* Summary */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-red-50 dark:bg-red-950/20 rounded-lg">
                      <div className="text-2xl font-bold text-red-600">{result.highIssues}</div>
                      <div className="text-xs text-muted-foreground">High</div>
                    </div>
                    <div className="text-center p-3 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg">
                      <div className="text-2xl font-bold text-yellow-600">{result.mediumIssues}</div>
                      <div className="text-xs text-muted-foreground">Medium</div>
                    </div>
                    <div className="text-center p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">{result.lowIssues}</div>
                      <div className="text-xs text-muted-foreground">Low</div>
                    </div>
                  </div>

                  {/* File Info */}
                  {result.fileInfo && (
                    <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                      <div className="flex items-center gap-2">
                        <File className="h-4 w-4" />
                        <span className="font-medium">{result.fileInfo.name}</span>
                      </div>
                      <div className="text-xs text-muted-foreground grid grid-cols-2 gap-2">
                        <div>Size: {(result.fileInfo.size / 1024).toFixed(1)} KB</div>
                        <div>Type: {result.fileInfo.type}</div>
                        <div className="col-span-2">Hash: {result.fileInfo.hash.substring(0, 16)}...</div>
                      </div>
                    </div>
                  )}

                  {/* Scan Info */}
                  <div className="text-sm text-muted-foreground text-center">
                    Scan completed in {result.scanTime.toFixed(2)} seconds
                  </div>

                  {/* Vulnerabilities */}
                  <div className="space-y-4">
                    <h4 className="font-medium">Findings</h4>
                    {result.vulnerabilities.map((vuln, index) => (
                      <div key={index} className="border rounded-lg p-4 space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            {getVulnerabilityIcon(vuln.type)}
                            <span className="font-medium">{vuln.description}</span>
                          </div>
                          <Badge className={getSeverityColor(vuln.severity)}>
                            {vuln.severity.toUpperCase()}
                          </Badge>
                        </div>
                        
                        {vuln.line && (
                          <div className="text-sm text-muted-foreground">
                            Line: {vuln.line}
                          </div>
                        )}
                        
                        <div className="space-y-2">
                          {vuln.payload && (
                            <div className="text-sm">
                              <span className="font-medium">Test Payload:</span>
                              <code className="ml-2 bg-muted px-2 py-1 rounded text-xs">
                                {vuln.payload}
                              </code>
                            </div>
                          )}
                          <div className="text-sm">
                            <span className="font-medium">Recommendation:</span>
                            <p className="mt-1 text-muted-foreground">{vuln.recommendation}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <Button
                    onClick={downloadReport}
                    variant="outline"
                    className="w-full hover-lift"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Report
                  </Button>
                </div>
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Enter code and click "Scan Code" to see results</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Card className="card-enhanced mt-6">
          <CardHeader>
            <CardTitle>Security Best Practices</CardTitle>
            <CardDescription>
              How to prevent common web vulnerabilities and malware
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold">Preventing XSS</h3>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li>• Use output encoding (HTML, JavaScript, CSS)</li>
                  <li>• Implement Content Security Policy (CSP)</li>
                  <li>• Validate and sanitize all user input</li>
                  <li>• Use modern frameworks with built-in protection</li>
                  <li>• Avoid innerHTML and document.write with user data</li>
                </ul>
              </div>
              <div className="space-y-4">
                <h3 className="font-semibold">Preventing SQL Injection</h3>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li>• Use prepared statements or parameterized queries</li>
                  <li>• Implement input validation and sanitization</li>
                  <li>• Use ORM frameworks when possible</li>
                  <li>• Apply principle of least privilege</li>
                  <li>• Regular security audits and testing</li>
                </ul>
              </div>
              <div className="space-y-4">
                <h3 className="font-semibold">Malware Prevention</h3>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li>• Scan all uploaded files with antivirus</li>
                  <li>• Use file type validation and restrictions</li>
                  <li>• Implement sandboxing for file execution</li>
                  <li>• Keep antivirus definitions updated</li>
                  <li>• Monitor file system changes</li>
                </ul>
              </div>
            </div>
            <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                <strong>Disclaimer:</strong> This scanner provides basic vulnerability and malware detection and should not replace comprehensive security testing. 
                Always use professional security tools and services for production applications. VirusTotal integration requires API key configuration.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}